import { Component, useCallback, useEffect, useMemo, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import {
  Pause, Play, RefreshCw, Trash2, Download,
  Wifi, WifiOff, Loader2,
} from 'lucide-react';
import { fetchChargerLogs, type OcppFrameRow } from '@/lib/api';
import { useLiveFrames } from '@/lib/useLiveFrames';
import {
  PairedRow, pairFrames, safeStringify,
} from '@/components/ocpp/PairedFrames';
import styles from './ChargerDetailPage.module.css';

interface LogsPanelProps {
  chargerId: string;
  chargerIdentity: string;
}

type DirectionFilter = 'all' | 'incoming' | 'outgoing';

/**
 * Paired-message OCPP log viewer matching the v1 admin reference.
 *
 * Frames are paired by `message_id` so each visible row shows the
 * full request AND its matching response side-by-side. Bodies
 * render inline (truncated ~200 chars with a "more" expander).
 *
 * Wrapped in an ErrorBoundary so a runtime error inside the panel
 * shows an actionable message instead of blanking the whole tab.
 */
export function LogsPanel({ chargerId, chargerIdentity }: LogsPanelProps) {
  return (
    <LogsErrorBoundary>
      <LogsPanelInner chargerId={chargerId} chargerIdentity={chargerIdentity} />
    </LogsErrorBoundary>
  );
}

function LogsPanelInner({ chargerId, chargerIdentity }: LogsPanelProps) {
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [search, setSearch] = useState('');
  const [historical, setHistorical] = useState<OcppFrameRow[]>([]);
  const [olderLoading, setOlderLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');

  const live = useLiveFrames(chargerIdentity, !paused);

  const reloadHistorical = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      const res = await fetchChargerLogs(chargerId, { limit: 100 });
      setHistorical(res.data);
      setHasMore(res.meta.nextBefore !== null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
    } finally {
      setRefreshing(false);
    }
  }, [chargerId]);

  useEffect(() => {
    setHistorical([]);
    setHasMore(true);
    void reloadHistorical();
  }, [reloadHistorical]);

  const loadOlder = async () => {
    if (olderLoading || !hasMore) return;
    setOlderLoading(true);
    try {
      const oldest = historical.at(-1)?.timestamp;
      const res = await fetchChargerLogs(chargerId, {
        limit: 100,
        ...(oldest ? { before: oldest } : {}),
      });
      setHistorical((prev) => [...prev, ...res.data]);
      setHasMore(res.meta.nextBefore !== null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load older');
    } finally {
      setOlderLoading(false);
    }
  };

  // Merge live + historical, dropping any malformed rows defensively
  // so one bad frame can't nuke the whole panel. Dedupe on
  // message_id + timestamp + direction.
  const merged = useMemo(() => {
    const seen = new Set<string>();
    const out: OcppFrameRow[] = [];
    for (const row of [...live.frames, ...historical]) {
      if (!row || typeof row.message_id !== 'string') continue;
      const key = `${row.message_id}|${row.timestamp}|${row.direction}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
    return out;
  }, [live.frames, historical]);

  const paired = useMemo(() => pairFrames(merged), [merged]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return paired.filter((p) => {
      if (direction !== 'all' && p.request.direction !== direction) return false;
      if (!q) return true;
      const hay = [
        p.request.action ?? '',
        p.request.charger_identity ?? '',
        safeStringify(p.request.payload),
        p.response ? safeStringify(p.response.payload) : '',
        p.response?.error_code ?? '',
        p.response?.error_description ?? '',
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [paired, direction, search]);

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocpp-logs-${chargerIdentity}-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.logsPanel}>
      <div className={styles.logsControls}>
        <button
          className={styles.logsCtl}
          onClick={() => void reloadHistorical()}
          disabled={refreshing}
          title="Refresh historical frames"
        >
          {refreshing
            ? <><Loader2 size={13} className={styles.cmdSpinner} /> Refreshing</>
            : <><RefreshCw size={13} /> Refresh</>}
        </button>
        <button
          className={styles.logsCtl}
          onClick={() => setPaused((p) => !p)}
          title={paused ? 'Resume live tail' : 'Pause live tail'}
        >
          {paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
        </button>
        <button
          className={styles.logsCtl}
          onClick={() => { live.clear(); setHistorical([]); setHasMore(true); }}
          title="Clear buffer"
        >
          <Trash2 size={13} /> Clear
        </button>
        <button
          className={styles.logsCtl}
          onClick={handleDownload}
          disabled={filtered.length === 0}
          title="Download filtered frames as JSON"
        >
          <Download size={13} /> Download
        </button>

        <select
          className={styles.logsSelect}
          value={direction}
          onChange={(e) => setDirection(e.target.value as DirectionFilter)}
        >
          <option value="all">All directions</option>
          <option value="incoming">From charger</option>
          <option value="outgoing">To charger</option>
        </select>

        <input
          className={styles.logsSearch}
          placeholder="Search action, identity, or body…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <span className={`${styles.logsState} ${live.state.status === 'open' ? styles.logsStateOk : styles.logsStateOff}`}>
          {live.state.status === 'open'
            ? <><Wifi size={12} /> Live</>
            : live.state.status === 'connecting'
              ? <><Loader2 size={12} className={styles.cmdSpinner} /> Connecting</>
              : <><WifiOff size={12} /> {live.state.status}</>}
        </span>
      </div>

      {error && <div className={styles.logsError}>{error}</div>}

      <div className={styles.logsList}>
        {filtered.length === 0 && (
          <p className={styles.logsEmpty}>
            {paired.length === 0
              ? `No frames yet. Live tail is ${paused ? 'paused' : live.state.status}.`
              : 'No frames match the current filter.'}
          </p>
        )}
        {filtered.map((p, idx) => (
          <PairedRow
            key={`${p.messageId}|${p.request.timestamp}|${idx}`}
            pair={p}
          />
        ))}
      </div>

      <div className={styles.logsFooter}>
        <button
          className={styles.logsCtl}
          onClick={loadOlder}
          disabled={olderLoading || !hasMore}
        >
          {olderLoading
            ? <><Loader2 size={13} className={styles.cmdSpinner} /> Loading…</>
            : hasMore
              ? <><RefreshCw size={13} /> Load older</>
              : 'No older frames'}
        </button>
      </div>
    </div>
  );
}

// ── Error boundary ────────────────────────────────────────────────

interface BoundaryState {
  err: Error | null;
}

class LogsErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { err: null };

  static getDerivedStateFromError(err: Error): BoundaryState {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[LogsPanel crash]', err, info);
  }

  render(): ReactNode {
    if (this.state.err) {
      return (
        <div className={styles.logsError} style={{ padding: 16 }}>
          <strong>Logs panel failed to render.</strong>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8, fontSize: 11 }}>
            {this.state.err.message}
          </pre>
          <button
            type="button"
            className={styles.logsCtl}
            style={{ marginTop: 8 }}
            onClick={() => this.setState({ err: null })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pairing logic + row rendering live in
// `@/components/ocpp/PairedFrames` — imported above. SessionDetailPage
// reuses the same components for its per-session frame timeline.
